// src/features/auth/userState.ts
import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Hub } from "aws-amplify/utils";
import { loadUserInfo, saveAttributes, doSignOut } from "./api";
import type { IUserInfo } from "./types";

export const authKeys = {
    root: ["auth"] as const,
    info: () => [...authKeys.root, "userInfo"] as const, // single state node
};

// READ: one hook returns user + attrs together
export function useUserInfo() {
    const { authStatus } = useAuthenticator((c) => [c.authStatus]);
    return useQuery<IUserInfo>({
        queryKey: authKeys.info(),
        queryFn: loadUserInfo,
        enabled: authStatus === "authenticated",
        staleTime: 5 * 60 * 1000,
    });
}

export function useSetUserAttrs() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: Record<string, string>) => saveAttributes(patch),
        onMutate: async (patch) => {
            await qc.cancelQueries({ queryKey: authKeys.info() });
            const prev = qc.getQueryData<IUserInfo>(authKeys.info());
            if (prev) {
                // optimistic merge
                qc.setQueryData<IUserInfo>(authKeys.info(), {
                    ...prev,
                    attrs: { ...prev.attrs, ...patch },
                });
            }
            return { prev };
        },
        onError: (_e, _patch, ctx) => {
            if (ctx?.prev) qc.setQueryData(authKeys.info(), ctx.prev);
        },
        onSuccess: (_res, _patch) => {
            // Option A: refetch to be 100% in sync with Cognito
            qc.invalidateQueries({ queryKey: authKeys.info() });
            // (Option B: keep optimistic state only — skip invalidate)
        },
    });
}

// ACTION: sign out and clear the single auth cache
export function useSignOut() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: doSignOut,
        onSettled: () => {
            qc.removeQueries({ queryKey: authKeys.root, exact: false });
            // Profile status is account-sensitive; never reuse it across sessions.
            qc.removeQueries({ queryKey: ["profile"], exact: false });
        },
    });
}

// Bridge Amplify auth events <-> single cache (optional but nice)
export function useAuthUserBridge() {
    const qc = useQueryClient();
    const { authStatus } = useAuthenticator((c) => [c.authStatus]);

    // seed on authenticated / clear otherwise
    useEffect(() => {
        if (authStatus === "authenticated") {
            loadUserInfo()
                .then((info) => qc.setQueryData(authKeys.info(), info))
                .catch(() => { });
        } else {
            qc.removeQueries({ queryKey: authKeys.root, exact: false });
        }
    }, [authStatus, qc]);

    // listen to Hub events
    useEffect(() => {
        const unsubscribe = Hub.listen("auth", async ({ payload }) => {
            const ev = payload?.event;
            try {
                if (ev === "signedIn") {
                    qc.setQueryData(authKeys.info(), await loadUserInfo());
                }
                if (ev === "signedOut" || ev === "tokenRefresh_failure") {
                    qc.removeQueries({ queryKey: authKeys.root, exact: false });
                }
            } catch {
                // ignore
            }
        });
        return unsubscribe;
    }, [qc]);
}

// Tiny boot component (mount once after sign-in)
export function AuthUserBoot() {
    useAuthUserBridge();
    return null;
}
